import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

import { rpc } from "../../services/datos";
import Esqueleto from "../../shared/components/Esqueleto";

function EncuestaPublica() {
  const { id } = useParams();
  const [encuesta, setEncuesta] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [correo, setCorreo] = useState("");
  const [cargando, setCargando] = useState(true);
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Sin sesión: solo devuelve la encuesta si está activa y dentro de sus fechas.
    rpc("encuesta_publica", { p_id: Number(id) })
      .then((data) => {
        setEncuesta(data?.encuesta || null);
        setPreguntas(data?.preguntas || []);
      })
      .catch(() => setEncuesta(null))
      .finally(() => setCargando(false));
  }, [id]);

  const handleRespuesta = (preguntaId, valor) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: valor }));
  };

  const enviar = () => {
    if (!encuesta.anonima && !correo.includes("@")) {
      setError("Por favor escribe tu correo");
      return;
    }
    for (const p of preguntas) {
      if (p.requerida && !respuestas[p.id]) {
        setError(`Por favor responde: "${p.texto}"`);
        return;
      }
    }
    setError("");
    setEnviando(true);

    const payload = {
      p_id: Number(id),
      p_email: encuesta.anonima ? null : correo.trim(),
      p_respuestas: Object.entries(respuestas).map(([pregunta_id, respuesta]) => ({
        pregunta_id: parseInt(pregunta_id),
        respuesta: Array.isArray(respuesta) ? respuesta.join(", ") : String(respuesta),
      })),
    };

    rpc("responder_encuesta", payload)
      .then(() => setEnviado(true))
      .catch((e) => {
        setError(e.message || "Error al enviar. Intenta de nuevo.");
        setEnviando(false);
      });
  };

  if (cargando) {
    return (
      <div className="min-h-screen bg-oscuro flex items-center justify-center">
        <Esqueleto tipo="texto" className="w-full max-w-md" texto="Cargando encuesta…" />
      </div>
    );
  }

  if (!encuesta || !encuesta.id) {
    return (
      <div className="min-h-screen bg-oscuro flex items-center justify-center">
        <p className="text-red-400 text-lg">Encuesta no encontrada</p>
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="min-h-screen bg-oscuro flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center rounded-2xl border border-white/10 bg-zinc-950 p-6 sm:p-8 ventana-panel">
          <div className="mx-auto mb-6 h-px w-16 bg-white/60" />
          <h2 className="titulo-ventana mb-2">¡Gracias!</h2>
          <p className="text-white">Tus respuestas han sido registradas.</p>
          <p className="text-gray-400 text-sm mt-2">Que Dios te bendiga</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oscuro p-4">
      <div className="max-w-2xl mx-auto">

        <div className="text-center mb-8 pt-6">
          <h1 className="titulo-pagina">Mi Casa</h1>
          <p className="text-gray-400 text-sm">La familia que buscabas</p>
        </div>

        <div className="panel border-white/25 p-6 mb-6">
          <h2 className="text-white text-2xl font-bold mb-2">{encuesta.titulo}</h2>
          {encuesta.descripcion && <p className="text-gray-400">{encuesta.descripcion}</p>}
          {encuesta.anonima && (
            <span className="inline-block mt-3 text-xs bg-oscuro px-3 py-1 rounded-full text-gray-400">
              Esta encuesta es anónima
            </span>
          )}
        </div>

        {!encuesta.anonima && (
          <div className="panel p-6 mb-6">
            <label htmlFor="correo-encuesta" className="text-gray-400 text-sm mb-2 block">Tu correo *</label>
            <input
              id="correo-encuesta"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="El correo con el que estás registrado"
              className="campo"
            />
          </div>
        )}

        {preguntas.map((p, i) => (
          <div key={p.id} className="panel p-6 mb-4">
            <p className="text-white font-semibold mb-4">
              {i + 1}. {p.texto}
              {p.requerida && <span className="text-dorado ml-1">*</span>}
            </p>

            {p.tipo === "texto" && (
              <textarea
                value={respuestas[p.id] || ""}
                onChange={(e) => handleRespuesta(p.id, e.target.value)}
                rows={3}
                placeholder="Escribe tu respuesta..."
                className="campo"
              />
            )}

            {p.tipo === "si_no" && (
              <div className="flex gap-4">
                {["si", "no"].map((op) => (
                  <button
                    key={op}
                    onClick={() => handleRespuesta(p.id, op)}
                    className={`flex-1 py-3 rounded-xl font-bold transition border
                      ${respuestas[p.id] === op
                        ? "bg-dorado text-oscuro border-dorado"
                        : "border-gray-600 text-gray-300 hover:border-dorado"
                      }`}
                  >
                    {op === "si" ? "Sí" : "No"}
                  </button>
                ))}
              </div>
            )}

            {p.tipo === "opcion_multiple" && (
              <div className="space-y-2">
                {p.opciones_pregunta?.map((op) => (
                  <button
                    key={op.id}
                    onClick={() => handleRespuesta(p.id, op.texto)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition border
                      ${respuestas[p.id] === op.texto
                        ? "bg-dorado text-oscuro border-dorado font-bold"
                        : "border-gray-600 text-gray-300 hover:border-dorado"
                      }`}
                  >
                    {op.texto}
                  </button>
                ))}
              </div>
            )}

            {p.tipo === "escala" && (
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => handleRespuesta(p.id, String(n))}
                    className={`flex-1 py-3 rounded-xl font-bold transition border
                      ${respuestas[p.id] === String(n)
                        ? "bg-dorado text-oscuro border-dorado"
                        : "border-gray-600 text-gray-300 hover:border-dorado"
                      }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        {error && (
          <div className="bg-red-900 border border-red-700 rounded-xl p-4 mb-4">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={enviar}
          disabled={enviando}
          className="w-full mb-8 btn-primario btn-lg"
        >
          {enviando ? "Enviando..." : "Enviar respuestas"}
        </button>

      </div>
    </div>
  );
}

export default EncuestaPublica;